// /join/:inviteToken — where a guest lands from the host's link. Outside the
// app layout and without any Rumio session: a name, a device check, the call.

import { useState, type FormEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { forgetGuest, guestErrorMessage, joinAsGuest, savedGuest, type StudioSession } from '../lib/studio/studio-api';
import { DeviceCheck } from '../components/studio/DeviceCheck';
import { StudioCall } from '../components/studio/StudioCall';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { studioFlags } from './StudioRoomPage';

type Stage = { at: 'name' } | { at: 'check'; session: StudioSession; name: string } | { at: 'call'; session: StudioSession; name: string; stream: MediaStream } | { at: 'done'; kicked: boolean };

const NAME_KEY = 'studio:guest-name';

export function GuestJoinPage() {
  const { inviteToken = '' } = useParams<{ inviteToken: string }>();
  const flags = studioFlags(useLocation().search);
  const saved = savedGuest(inviteToken);
  const [stage, setStage] = useState<Stage>(() =>
    saved ? { at: 'check', session: saved, name: readName() ?? 'Guest' } : { at: 'name' },
  );

  return (
    <main className="min-h-screen bg-bg px-4 py-8 text-text">
      <div className="mx-auto max-w-5xl space-y-6">
        <header>
          <p className="text-sm text-muted">PodcastWiz Studio</p>
          <h1 className="text-2xl font-semibold">{'session' in stage ? stage.session.room.name : 'Join the recording'}</h1>
        </header>
        {stage.at === 'name' && <NameStep inviteToken={inviteToken} onJoined={(session, name) => setStage({ at: 'check', session, name })} />}
        {stage.at === 'check' && (
          <DeviceCheck
            name={stage.name}
            allowTestSignal={flags.testSource}
            joinLabel="Join the call"
            onJoin={(stream) => setStage({ at: 'call', session: stage.session, name: stage.name, stream })}
          />
        )}
        {stage.at === 'call' && (
          <StudioCall
            session={stage.session}
            displayName={stage.name}
            stream={stage.stream}
            isHost={false}
            relayOnly={flags.relay}
            simulateOfflineS={flags.simulateOfflineS}
            onLeave={(reason) => {
              if (reason === 'kicked') forgetGuest(inviteToken);
              setStage({ at: 'done', kicked: reason === 'kicked' });
            }}
          />
        )}
        {stage.at === 'done' && (
          <div className="rounded-xl border border-border p-6 text-center">
            <h2 className="text-lg font-semibold">{stage.kicked ? 'The host removed you from the call.' : 'You left the call.'}</h2>
            {!stage.kicked && (
              <Button className="mt-4" onClick={() => location.reload()}>
                Rejoin
              </Button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

function readName(): string | null {
  try {
    return localStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

function NameStep({ inviteToken, onJoined }: { inviteToken: string; onJoined: (s: StudioSession, name: string) => void }) {
  const [name, setName] = useState(() => readName() ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    setBusy(true);
    setError(null);
    try {
      const session = await joinAsGuest(inviteToken, n);
      try {
        localStorage.setItem(NAME_KEY, n);
      } catch {
        // not remembered; fine
      }
      onJoined(session, n);
    } catch (err) {
      setError(guestErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-4 rounded-xl border border-border p-5">
      <p className="text-sm text-muted">You’ve been invited to record. No account needed — just your name, then check your camera and mic.</p>
      <TextField label="Your name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} autoFocus />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <Button type="submit" loading={busy} disabled={!name.trim()}>
        Continue
      </Button>
    </form>
  );
}
